import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { StoreProvider, useStore } from './stores';
import { rootStore } from './stores/RootStore';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { ThemeProvider } from './theme/ThemeProvider';
import Users from './pages/Users';
import Drivers from './pages/Drivers';
import DriverManagement from './pages/DriverManagement';
import Vehicles from './pages/Vehicles';
import History from './pages/History';
import Settings from './pages/Settings';
import Balance from './pages/Balance';
import Profile from './pages/Profile';
import { useWebSocket } from './hooks/useWebSocket';
import { DriverLocation } from './stores/MainStore';


const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mainStore } = useStore();
  
  const { isConnected, sendMessage } = useWebSocket({
    autoConnect: true,
    onConnected: () => {
      sendMessage({ type: 'subscribe_all_drivers' });
    },
    onDisconnected: () => {
      // console.log('WebSocket desconectado globalmente');
    },
    onMessage: (data) => {
      if (data.type === 'location_update') {
        mainStore.updateDriverLocation(data as unknown as DriverLocation);
      } else if (data.type === 'initial_drivers_data' || data.type === 'all_drivers_locations') {
        mainStore.updateDriverLocations(data.drivers as DriverLocation[]);
      } else if (data.type === 'driver_status_changed') {
        const { driver_id, is_online, is_available } = data;
        const driver = mainStore.driverLocations.find(d => d.driver_id === driver_id);
        if (driver) {
          const updatedDriver = {
            ...driver,
            is_online,
            is_available
          };
          mainStore.updateDriverLocation(updatedDriver);
        }
      }
    }
  });

  
  useEffect(() => {
    
    (window as any).globalWebSocket = { isConnected, sendMessage };
    return () => {
      delete (window as any).globalWebSocket;
    };
  }, [isConnected, sendMessage]);

  return <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authStore } = useStore();

  if (!authStore.isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <WebSocketProvider>
      {children}
    </WebSocketProvider>
  );
};

const AppContent: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        } />
        <Route path="/drivers/management" element={
          <ProtectedRoute>
            <Drivers />
          </ProtectedRoute>
        } />
        <Route path="/drivers" element={
          <ProtectedRoute>
            <DriverManagement />
          </ProtectedRoute>
        } />
        <Route path="/vehicles" element={
          <ProtectedRoute>
            <Vehicles />
          </ProtectedRoute>
        } />
        <Route path="/history" element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        } />
        <Route path="/balance" element={
          <ProtectedRoute>
            <Balance />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </BrowserRouter>
  );
};

const App: React.FC = () => {
  return (
    <StoreProvider value={rootStore}>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </StoreProvider>
  );
};

export default observer(App);